import { useDraggable } from "@dnd-kit/core";
import { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from "react";

import css from "./floating_dock.module.scss";

export type DragHandleProps = {
  ref: (node: HTMLElement | null) => void;
  listeners: ReturnType<typeof useDraggable>["listeners"];
  attributes: ReturnType<typeof useDraggable>["attributes"];
};

type FloatingDockDraggableProps = {
  id: string;
  isExpanded: boolean;
  areHeaderControlsVisible: boolean;
  anchorStyle: CSSProperties;
  morphStyle: CSSProperties;
  transitionStyle: CSSProperties;
  panelCursor?: string;
  onMouseMove?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onMouseDown?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  renderCollapsed: () => ReactNode;
  renderExpanded: (dragHandleProps: DragHandleProps, areHeaderControlsVisible: boolean) => ReactNode;
};

export const FloatingDockDraggable = ({
  id,
  isExpanded,
  areHeaderControlsVisible,
  anchorStyle,
  morphStyle,
  transitionStyle,
  panelCursor,
  onMouseMove,
  onMouseDown,
  renderCollapsed,
  renderExpanded
}: FloatingDockDraggableProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform } = useDraggable({ id });

  const dragTransform: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};

  const dockDragProps = isExpanded ? {} : { ...listeners, ...attributes };
  const dragHandleProps: DragHandleProps = { ref: setActivatorNodeRef, listeners, attributes };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={setNodeRef}
      className={css.dock}
      data-qa-id="FloatingExpandableBubbleDock"
      style={{
        ...anchorStyle,
        ...morphStyle,
        ...dragTransform,
        ...transitionStyle,
        ...(isExpanded && panelCursor ? { cursor: panelCursor } : {})
      }}
      onMouseMove={isExpanded ? onMouseMove : undefined}
      onMouseDown={isExpanded ? onMouseDown : undefined}
      {...dockDragProps}
      tabIndex={isExpanded ? undefined : -1}
    >
      {isExpanded ? renderExpanded(dragHandleProps, areHeaderControlsVisible) : renderCollapsed()}
    </div>
  );
};
